import React, { useMemo, useState } from "react";
import {
    ChevronDown,
    ChevronsUpDown,
    ChevronUp,
    Download,
    Filter,
    Plus,
    Search,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "@/components/ui/pagination";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useDebounce } from "@/hooks/useDebounce";

/**
 * Fully-featured, reusable DataTable.
 *
 * Props:
 *  - columns: [{ key, header, sortable?, className?, cell?: (row)=>ReactNode }]
 *  - data: rows (or use `query` with `{items, total, page, page_size, total_pages}`)
 *  - query: paginated payload from backend (preferred)
 *  - loading: boolean
 *  - onSearch: (text)=>void        // if provided, shows search input
 *  - onPageChange: (page)=>void
 *  - onSortChange: ({sortBy, sortOrder})=>void
 *  - onRowClick: (row)=>void
 *  - actions: React node rendered in toolbar right side
 *  - toolbarLeft: React node rendered in toolbar left side (extra filters)
 *  - emptyState: {title, description, cta}
 *  - filtersActive: array of active filter chips
 *  - onCreate: () => void (renders "Novo" button)
 *  - createLabel: string (defaults to "Novo")
 *  - dataTestId: string prefix for test IDs
 */
export function DataTable({
    columns,
    query,
    data,
    loading,
    onSearch,
    onPageChange,
    onSortChange,
    onRowClick,
    actions,
    toolbarLeft,
    emptyState,
    filtersActive = [],
    onCreate,
    createLabel = "Novo",
    dataTestId = "datatable",
    initialSort,
}) {
    const [searchText, setSearchText] = useState("");
    const debounced = useDebounce(searchText, 350);
    React.useEffect(() => {
        if (onSearch) onSearch(debounced);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debounced]);

    const [sort, setSort] = useState(initialSort || { sortBy: null, sortOrder: "desc" });

    const rows = data ?? query?.items ?? [];
    const total = query?.total ?? rows.length;
    const page = query?.page ?? 1;
    const totalPages = query?.total_pages ?? 1;

    const pages = useMemo(() => {
        // Show up to 5 pages centered on current page
        const arr = [];
        const start = Math.max(1, page - 2);
        const end = Math.min(totalPages || 1, start + 4);
        for (let i = start; i <= end; i++) arr.push(i);
        return arr;
    }, [page, totalPages]);

    function toggleSort(colKey) {
        const next =
            sort.sortBy === colKey
                ? { sortBy: colKey, sortOrder: sort.sortOrder === "asc" ? "desc" : "asc" }
                : { sortBy: colKey, sortOrder: "asc" };
        setSort(next);
        onSortChange?.(next);
    }

    return (
        <div className="space-y-3">
            {/* Toolbar */}
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-1 flex-wrap items-center gap-2">
                    {onSearch && (
                        <div className="relative w-full sm:max-w-sm">
                            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                type="search"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                placeholder="Buscar…"
                                className="pl-9 h-9"
                                data-testid={`${dataTestId}-search-input`}
                            />
                        </div>
                    )}
                    {toolbarLeft}
                    {filtersActive.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1.5">
                            {filtersActive.map((chip, i) => (
                                <Badge key={`filter-chip-${chip}-${i}`} variant="secondary" className="gap-1">
                                    <Filter className="h-3 w-3" />
                                    <span>{chip}</span>
                                </Badge>
                            ))}
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {actions}
                    <Button
                        variant="outline"
                        size="sm"
                        disabled
                        title="Em breve"
                        data-testid={`${dataTestId}-export-button`}
                    >
                        <Download className="mr-2 h-4 w-4" /> Exportar
                    </Button>
                    {onCreate && (
                        <Button
                            size="sm"
                            onClick={onCreate}
                            className="bg-secondary text-secondary-foreground hover:opacity-90"
                            data-testid={`${dataTestId}-new-button`}
                        >
                            <Plus className="mr-2 h-4 w-4" />
                            {createLabel}
                        </Button>
                    )}
                </div>
            </div>

            {/* Table */}
            <div className="overflow-hidden rounded-xl border bg-card">
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                {columns.map((col) => (
                                    <TableHead
                                        key={col.key}
                                        className={cn("whitespace-nowrap", col.className)}
                                    >
                                        {col.sortable ? (
                                            <button
                                                type="button"
                                                onClick={() => toggleSort(col.sortKey || col.key)}
                                                className="inline-flex items-center gap-1 hover:text-foreground"
                                                data-testid={`${dataTestId}-sort-${col.key}`}
                                            >
                                                {col.header}
                                                {sort.sortBy === (col.sortKey || col.key) ? (
                                                    sort.sortOrder === "asc" ? (
                                                        <ChevronUp className="h-3.5 w-3.5" />
                                                    ) : (
                                                        <ChevronDown className="h-3.5 w-3.5" />
                                                    )
                                                ) : (
                                                    <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
                                                )}
                                            </button>
                                        ) : (
                                            col.header
                                        )}
                                    </TableHead>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                Array.from({ length: 6 }).map((_, r) => (
                                    <TableRow key={`skeleton-row-${r}`}>
                                        {columns.map((c) => (
                                            <TableCell key={c.key} className="py-3">
                                                <Skeleton className="h-4 w-32" />
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : rows.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={columns.length}
                                        className="h-40 text-center"
                                        data-testid={`${dataTestId}-empty-state`}
                                    >
                                        <div className="mx-auto max-w-sm space-y-2">
                                            <div className="text-sm font-medium">
                                                {emptyState?.title || "Nenhum resultado"}
                                            </div>
                                            <div className="text-xs text-muted-foreground">
                                                {emptyState?.description ||
                                                    "Tente ajustar os filtros ou criar um novo registro."}
                                            </div>
                                            {emptyState?.cta && onCreate && (
                                                <div className="pt-2">
                                                    <Button
                                                        size="sm"
                                                        onClick={onCreate}
                                                        className="bg-secondary text-secondary-foreground hover:opacity-90"
                                                        data-testid={`${dataTestId}-empty-create-button`}
                                                    >
                                                        <Plus className="mr-2 h-4 w-4" />
                                                        {emptyState.cta}
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                rows.map((row, idx) => (
                                    <TableRow
                                        key={row.id || idx}
                                        onClick={() => onRowClick?.(row)}
                                        className={cn(
                                            "h-11",
                                            onRowClick && "cursor-pointer hover:bg-[color:hsl(var(--secondary)/0.06)]",
                                        )}
                                        data-testid={`${dataTestId}-row-${idx}`}
                                    >
                                        {columns.map((c) => (
                                            <TableCell key={c.key} className={cn("py-2", c.className)}>
                                                {c.cell ? c.cell(row) : row[c.key]}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>

            {/* Footer */}
            {(total > 0 || onPageChange) && (
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-xs text-muted-foreground">
                        {total > 0 ? `${total} registro${total === 1 ? "" : "s"}` : "—"}
                    </div>
                    {onPageChange && totalPages > 1 && (
                        <Pagination data-testid={`${dataTestId}-pagination`}>
                            <PaginationContent>
                                <PaginationItem>
                                    <PaginationPrevious
                                        onClick={() => page > 1 && onPageChange(page - 1)}
                                        aria-disabled={page <= 1}
                                        className={page <= 1 ? "pointer-events-none opacity-50" : ""}
                                    />
                                </PaginationItem>
                                {pages.map((p) => (
                                    <PaginationItem key={p}>
                                        <PaginationLink
                                            isActive={p === page}
                                            onClick={() => onPageChange(p)}
                                            data-testid={`${dataTestId}-page-${p}`}
                                        >
                                            {p}
                                        </PaginationLink>
                                    </PaginationItem>
                                ))}
                                <PaginationItem>
                                    <PaginationNext
                                        onClick={() => page < totalPages && onPageChange(page + 1)}
                                        aria-disabled={page >= totalPages}
                                        className={
                                            page >= totalPages ? "pointer-events-none opacity-50" : ""
                                        }
                                    />
                                </PaginationItem>
                            </PaginationContent>
                        </Pagination>
                    )}
                </div>
            )}
        </div>
    );
}
